import React, { useState, useEffect } from 'react';
import {
  Card, Button, Modal, Form, Select, DatePicker, Row, Col, Space,
  Typography, Tag, Spin, message, Descriptions, Table, Empty
} from 'antd';
import {
  FileTextOutlined, FilePdfOutlined, FileExcelOutlined,
  DownloadOutlined, EyeOutlined, ReloadOutlined
} from '@ant-design/icons';
import { reportService } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const Reports = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState('ytd');
  const [form] = Form.useForm();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-indexed
  const currentQuarter = Math.floor(currentMonth / 3) + 1;

  // Generate date range presets
  const getDateRangePresets = () => {
    const presets = [
      { value: 'ytd', label: `YTD ${currentYear}`, dates: [dayjs(`${currentYear}-01-01`), dayjs()] },
      { value: 'last_12_months', label: 'Last 12 Months', dates: [dayjs().subtract(12, 'month'), dayjs()] },
      { value: 'last_6_months', label: 'Last 6 Months', dates: [dayjs().subtract(6, 'month'), dayjs()] },
      { value: 'last_3_months', label: 'Last 3 Months', dates: [dayjs().subtract(3, 'month'), dayjs()] },
    ];

    // Add current year quarters (only completed quarters + current)
    for (let q = 1; q <= currentQuarter; q++) {
      const startMonth = (q - 1) * 3;
      const endMonth = q * 3 - 1;
      presets.push({
        value: `q${q}_${currentYear}`,
        label: `Q${q} ${currentYear}`,
        dates: [
          dayjs(`${currentYear}-${String(startMonth + 1).padStart(2, '0')}-01`),
          q === currentQuarter ? dayjs() : dayjs(`${currentYear}-${String(endMonth + 1).padStart(2, '0')}-01`).endOf('month')
        ]
      });
    }

    // Add full years
    for (let y = currentYear; y >= currentYear - 2; y--) {
      if (y === currentYear) continue; // Skip current year (use YTD instead)
      presets.push({
        value: `full_${y}`,
        label: `Full Year ${y}`,
        dates: [dayjs(`${y}-01-01`), dayjs(`${y}-12-31`)]
      });
    }

    // Add previous year quarters
    const prevYear = currentYear - 1;
    for (let q = 1; q <= 4; q++) {
      const startMonth = (q - 1) * 3;
      presets.push({
        value: `q${q}_${prevYear}`,
        label: `Q${q} ${prevYear}`,
        dates: [
          dayjs(`${prevYear}-${String(startMonth + 1).padStart(2, '0')}-01`),
          dayjs(`${prevYear}-${String(startMonth + 3).padStart(2, '0')}-01`).endOf('month')
        ]
      });
    }

    return presets;
  };

  const dateRangePresets = getDateRangePresets();

  const handleDateRangePresetChange = (value) => {
    setDateRangePreset(value);
    const preset = dateRangePresets.find(p => p.value === value);
    if (preset?.dates) {
      form.setFieldsValue({ dateRange: preset.dates });
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await reportService.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      message.error('Failed to load report templates');
    } finally {
      setLoading(false);
    }
  };

  const getFormatIcon = (format) => {
    switch (format) {
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'excel':
        return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      case 'csv':
        return <FileTextOutlined style={{ color: '#1890ff' }} />;
      default:
        return <FileTextOutlined />;
    }
  };

  const getFormatColor = (format) => {
    switch (format) {
      case 'pdf':
        return 'red';
      case 'excel':
        return 'green';
      case 'csv':
        return 'blue';
      default:
        return 'default';
    }
  };

  const handlePreview = async (template) => {
    setSelectedTemplate(template);
    setPreviewLoading(true);
    setPreviewModalVisible(true);

    try {
      // Pass default YTD date range for preview
      const ytdPreset = dateRangePresets.find(p => p.value === 'ytd');
      const params = {};
      if (ytdPreset?.dates) {
        params.date_from = ytdPreset.dates[0].toISOString();
        params.date_to = ytdPreset.dates[1].toISOString();
      }
      const data = await reportService.preview(template.id, params);
      setPreviewData(data);
    } catch (error) {
      console.error('Failed to load preview:', error);
      message.error('Failed to load report preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadClick = (template) => {
    setSelectedTemplate(template);
    form.resetFields();

    // Set default date range to YTD
    const ytdPreset = dateRangePresets.find(p => p.value === 'ytd');
    setDateRangePreset('ytd');

    form.setFieldsValue({
      format: template.available_formats[0],
      dateRange: ytdPreset?.dates || null
    });
    setDownloadModalVisible(true);
  };

  const handleDownload = async () => {
    try {
      const values = await form.validateFields();
      setDownloadLoading(true);

      const params = {
        format: values.format
      };

      if (values.dateRange) {
        params.date_from = values.dateRange[0].toISOString();
        params.date_to = values.dateRange[1].toISOString();
      }

      if (values.year) {
        params.year = values.year;
      }

      if (values.month) {
        params.month = values.month;
      }

      await reportService.download(selectedTemplate.id, values.format, params);
      message.success('Report downloaded successfully');
      setDownloadModalVisible(false);
    } catch (error) {
      console.error('Failed to download report:', error);
      message.error('Failed to download report');
    } finally {
      setDownloadLoading(false);
    }
  };

  const renderPreviewContent = () => {
    if (!previewData) return null;

    const { data } = previewData;

    switch (selectedTemplate?.id) {
      case 'spend_summary':
        return (
          <div>
            <Descriptions title="Summary" bordered size="small" column={2}>
              <Descriptions.Item label="Total Spend">
                ${data.totals?.total_spend?.toLocaleString() || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Total Budget">
                ${data.totals?.total_budget?.toLocaleString() || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 16 }}>Spend by Category</Title>
            <Table
              dataSource={data.by_category || []}
              columns={[
                { title: 'Category', dataIndex: 'category', key: 'category' },
                {
                  title: 'Spend',
                  dataIndex: 'spend',
                  key: 'spend',
                  render: (v) => `$${v?.toLocaleString() || 0}`
                },
                {
                  title: 'Budget',
                  dataIndex: 'budget',
                  key: 'budget',
                  render: (v) => v ? `$${v.toLocaleString()}` : '-'
                }
              ]}
              rowKey="category"
              pagination={false}
              size="small"
            />

            <Title level={5} style={{ marginTop: 16 }}>Top Vendors</Title>
            <Table
              dataSource={(data.by_vendor || []).slice(0, 5)}
              columns={[
                { title: 'Vendor', dataIndex: 'vendor', key: 'vendor' },
                {
                  title: 'Spend',
                  dataIndex: 'spend',
                  key: 'spend',
                  render: (v) => `$${v?.toLocaleString() || 0}`
                }
              ]}
              rowKey="vendor"
              pagination={false}
              size="small"
            />
          </div>
        );

      case 'vendor_scorecard':
        return (
          <div>
            <Descriptions title="Summary" bordered size="small" column={3}>
              <Descriptions.Item label="Vendors Scored">
                {data.summary?.total_vendors || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Avg Score">
                {data.summary?.avg_overall_score?.toFixed(1) || 0}
              </Descriptions.Item>
              <Descriptions.Item label="High Risk">
                {data.summary?.high_risk_count || 0}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 16 }}>Vendor Scores</Title>
            <Table
              dataSource={(data.vendors || []).slice(0, 10)}
              columns={[
                { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name' },
                { title: 'Overall', dataIndex: 'overall_score', key: 'overall_score', render: (v) => v?.toFixed(0) },
                { title: 'Quality', dataIndex: 'quality_score', key: 'quality_score', render: (v) => v?.toFixed(0) },
                { title: 'Delivery', dataIndex: 'delivery_score', key: 'delivery_score', render: (v) => v?.toFixed(0) },
                { title: 'Risk', dataIndex: 'risk_score', key: 'risk_score', render: (v) => v?.toFixed(0) }
              ]}
              rowKey="vendor_name"
              pagination={false}
              size="small"
            />
          </div>
        );

      case 'contract_status':
        return (
          <div>
            <Descriptions title="Summary" bordered size="small" column={2}>
              <Descriptions.Item label="Total Contracts">
                {data.summary?.total_contracts || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Total Value">
                ${data.summary?.total_value?.toLocaleString() || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Active">
                {data.summary?.active || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Expiring (30d)">
                <Tag color="red">{data.summary?.expiring_30 || 0}</Tag>
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 16 }}>Contracts</Title>
            <Table
              dataSource={(data.contracts || []).slice(0, 10)}
              columns={[
                { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
                { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name' },
                { title: 'End Date', dataIndex: 'end_date', key: 'end_date', render: (v) => v?.slice(0, 10) || '-' },
                { title: 'Days Left', dataIndex: 'days_until_expiry', key: 'days_until_expiry' },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (v) => (
                    <Tag color={v === 'expired' ? 'red' : v?.includes('expiring') ? 'orange' : 'green'}>
                      {v}
                    </Tag>
                  )
                }
              ]}
              rowKey="title"
              pagination={false}
              size="small"
            />
          </div>
        );

      case 'executive_summary':
        return (
          <div>
            <Descriptions title="Key Performance Indicators" bordered size="small" column={2}>
              <Descriptions.Item label="Total Vendors">
                {data.kpis?.total_vendors || 0}
              </Descriptions.Item>
              <Descriptions.Item label="YTD Spend">
                ${data.kpis?.ytd_spend?.toLocaleString() || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Active Contracts">
                {data.kpis?.active_contracts || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Expiring Soon">
                <Tag color="orange">{data.kpis?.contracts_expiring_soon || 0}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Potential Savings">
                ${data.kpis?.potential_savings?.toLocaleString() || 0}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 16 }}>Top Vendors by Spend</Title>
            <Table
              dataSource={data.top_vendors || []}
              columns={[
                { title: 'Vendor', dataIndex: 'name', key: 'name' },
                {
                  title: 'YTD Spend',
                  dataIndex: 'spend',
                  key: 'spend',
                  render: (v) => `$${v?.toLocaleString() || 0}`
                }
              ]}
              rowKey="name"
              pagination={false}
              size="small"
            />
          </div>
        );

      default:
        return <Text>Preview not available for this report type</Text>;
    }
  };

  const years = Array.from({ length: 3 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString()
  }));

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Reports
          </Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={loadTemplates}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {templates.map((template) => (
            <Col span={12} key={template.id}>
              <Card
                hoverable
                actions={[
                  <Button
                    key="preview"
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(template)}
                  >
                    Preview
                  </Button>,
                  <Button
                    key="download"
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadClick(template)}
                  >
                    Download
                  </Button>
                ]}
              >
                <Card.Meta
                  avatar={
                    <div style={{ fontSize: 32 }}>
                      {template.id === 'spend_summary' && <FileTextOutlined style={{ color: '#1890ff' }} />}
                      {template.id === 'vendor_scorecard' && <FileTextOutlined style={{ color: '#52c41a' }} />}
                      {template.id === 'contract_status' && <FileTextOutlined style={{ color: '#faad14' }} />}
                      {template.id === 'executive_summary' && <FileTextOutlined style={{ color: '#722ed1' }} />}
                    </div>
                  }
                  title={template.name}
                  description={
                    <div>
                      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                        {template.description}
                      </Paragraph>
                      <Space>
                        {template.available_formats.map((format) => (
                          <Tag key={format} color={getFormatColor(format)} icon={getFormatIcon(format)}>
                            {format.toUpperCase()}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>

      {templates.length === 0 && !loading && (
        <Empty description="No report templates available" />
      )}

      {/* Preview Modal */}
      <Modal
        title={`Preview: ${selectedTemplate?.name}`}
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              setPreviewModalVisible(false);
              handleDownloadClick(selectedTemplate);
            }}
          >
            Download
          </Button>
        ]}
      >
        <Spin spinning={previewLoading}>
          {previewData ? (
            <div>
              <Descriptions size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Generated">
                  {previewData.generated_at ? dayjs(previewData.generated_at).format('MMM D, YYYY h:mm A') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Rows">
                  {previewData.row_count || 0}
                </Descriptions.Item>
              </Descriptions>
              {renderPreviewContent()}
            </div>
          ) : (
            <Empty description="Loading preview..." />
          )}
        </Spin>
      </Modal>

      {/* Download Modal */}
      <Modal
        title={`Download: ${selectedTemplate?.name}`}
        open={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        onOk={handleDownload}
        confirmLoading={downloadLoading}
        okText="Download"
        okButtonProps={{ icon: <DownloadOutlined /> }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="format"
            label="Format"
            rules={[{ required: true, message: 'Select a format' }]}
          >
            <Select>
              {selectedTemplate?.available_formats.map((format) => (
                <Select.Option key={format} value={format}>
                  <Space>
                    {getFormatIcon(format)}
                    {format.toUpperCase()}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedTemplate?.id === 'vendor_scorecard' && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="year" label="Year">
                  <Select options={years} placeholder="Select year" allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="month" label="Month">
                  <Select options={months} placeholder="Select month" allowClear />
                </Form.Item>
              </Col>
            </Row>
          )}

          {(selectedTemplate?.id === 'spend_summary' || selectedTemplate?.id === 'contract_status' || selectedTemplate?.id === 'executive_summary') && (
            <>
              <Form.Item label="Quick Select">
                <Select
                  value={dateRangePreset}
                  onChange={handleDateRangePresetChange}
                  style={{ width: '100%' }}
                >
                  <Select.OptGroup label="Quick Ranges">
                    {dateRangePresets.filter(p => ['ytd', 'last_12_months', 'last_6_months', 'last_3_months'].includes(p.value)).map(preset => (
                      <Select.Option key={preset.value} value={preset.value}>
                        {preset.label}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label={`${currentYear} Quarters`}>
                    {dateRangePresets.filter(p => p.value.includes(`_${currentYear}`) && p.value.startsWith('q')).map(preset => (
                      <Select.Option key={preset.value} value={preset.value}>
                        {preset.label}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="Full Years">
                    {dateRangePresets.filter(p => p.value.startsWith('full_')).map(preset => (
                      <Select.Option key={preset.value} value={preset.value}>
                        {preset.label}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label={`${currentYear - 1} Quarters`}>
                    {dateRangePresets.filter(p => p.value.includes(`_${currentYear - 1}`) && p.value.startsWith('q')).map(preset => (
                      <Select.Option key={preset.value} value={preset.value}>
                        {preset.label}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                </Select>
              </Form.Item>

              <Form.Item name="dateRange" label="Date Range">
                <RangePicker
                  style={{ width: '100%' }}
                  onChange={() => setDateRangePreset(null)}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Reports;
